import React, { useEffect, useState } from 'react';
import { Alert, Badge, Card, Spinner, Table } from 'react-bootstrap';
import api from '../../api';

interface Parameter {
  name: string;
  in: string;
  required?: boolean;
  schema?: { type?: string };
  description?: string;
}

interface Operation {
  summary?: string;
  description?: string;
  parameters?: Parameter[];
  responses?: Record<string, { description?: string }>;
  tags?: string[];
}

interface PathItem {
  [method: string]: Operation;
}

interface OpenApiSpec {
  info?: { title?: string; version?: string; description?: string };
  servers?: { url?: string }[];
  paths?: Record<string, PathItem>;
}

const METHOD_COLORS: Record<string, string> = {
  get: 'primary',
  post: 'success',
  put: 'warning',
  patch: 'info',
  delete: 'danger',
};

const HTTP_METHODS = new Set(['get', 'post', 'put', 'patch', 'delete']);

const ApiDocsPage: React.FC = () => {
  const [spec, setSpec] = useState<OpenApiSpec | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get('/integ/v3/api-docs')
      .then(r => {
        const data = typeof r.data === 'string' ? JSON.parse(r.data) : r.data;
        setSpec(data);
      })
      .catch(e => {
        console.error('Erro ao carregar api-docs:', e);
        setError(e?.response?.data?.message || 'Erro ao carregar documentação');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !spec) {
    return <Alert variant="danger">{error || 'Documentação não disponível'}</Alert>;
  }

  const serverUrl = spec.servers?.[0]?.url || '';

  return (
    <div className="p-3">
      <h4 className="mb-1">{spec.info?.title || 'API de Integração'}</h4>
      {spec.info?.version && (
        <small className="text-muted">Versão {spec.info.version}</small>
      )}
      {spec.info?.description && (
        <p className="mt-2 text-muted">{spec.info.description}</p>
      )}

      {serverUrl && (
        <Alert variant="light" className="mt-3 border">
          <strong>URL Base:</strong>{' '}
          <code>{serverUrl}</code>
        </Alert>
      )}

      <div className="mt-4">
        {spec.paths && Object.entries(spec.paths).map(([path, methods]) => (
          Object.entries(methods)
            .filter(([method]) => HTTP_METHODS.has(method))
            .map(([method, operation]) => (
              <Card key={`${method}-${path}`} className="mb-3">
                <Card.Header className="d-flex align-items-center gap-2 py-2">
                  <Badge bg={METHOD_COLORS[method] || 'secondary'} className="text-uppercase" style={{ minWidth: 65, textAlign: 'center' }}>
                    {method}
                  </Badge>
                  <code style={{ fontSize: '0.95rem' }}>{path}</code>
                  {operation.summary && (
                    <span className="text-muted ms-2">— {operation.summary}</span>
                  )}
                </Card.Header>

                {(operation.parameters && operation.parameters.length > 0) && (
                  <Card.Body className="py-2 px-3">
                    <small className="text-muted fw-bold">Parâmetros</small>
                    <Table size="sm" className="mt-1 mb-0" borderless>
                      <thead>
                        <tr>
                          <th style={{ width: '20%' }}>Nome</th>
                          <th style={{ width: '10%' }}>Em</th>
                          <th style={{ width: '10%' }}>Tipo</th>
                          <th style={{ width: '10%' }}>Obrigatório</th>
                          <th>Descrição</th>
                        </tr>
                      </thead>
                      <tbody>
                        {operation.parameters.map((param, i) => (
                          <tr key={i}>
                            <td><code>{param.name}</code></td>
                            <td><small>{param.in}</small></td>
                            <td><small>{param.schema?.type || '-'}</small></td>
                            <td>{param.required ? <Badge bg="danger" pill>sim</Badge> : <small>não</small>}</td>
                            <td><small>{param.description || '-'}</small></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                )}

                {operation.responses && (
                  <Card.Footer className="py-1 px-3">
                    <small className="text-muted">
                      Respostas:{' '}
                      {Object.entries(operation.responses).map(([code, resp]) => (
                        <Badge key={code} bg={code.startsWith('2') ? 'success' : code.startsWith('4') ? 'warning' : 'secondary'} className="me-1">
                          {code} {resp.description}
                        </Badge>
                      ))}
                    </small>
                  </Card.Footer>
                )}
              </Card>
            ))
        ))}
      </div>
    </div>
  );
};

export default ApiDocsPage;
